import axiosInstance from './axiosInstance';

// Types
export interface Waypoint {
  id: string;
  lat: number;
  lng: number;
  address: string;
  order?: number;
  orderId?: string; // Reference to pickup order ID (backward compatibility)
  orderIds?: string[]; // Multiple order IDs for pharmacy pickups (backward compatibility)
  type?: 'pharmacy' | 'delivery' | 'other';
  pharmacy?: any;
  customer?: any;
  orders?: string[]; // Array of Order IDs (for delivery orders) - NEW
  pickupOrder?: string[]; // Array of PickupOrder IDs (for pickup orders) - NEW
  routeType?: 'pickup-orders' | 'orders'; // Route type for each waypoint - NEW
}

export interface AssignedDriver {
  _id: string;
  name: string;
  status: string;
  phone:  string;
  currentLocation: {
    lat: number;
    lng: number;
    address: string;
  };
}
export interface Route {
  _id?: string;
  id?: string;
  routeId?: string;
  name: string;
  description: string;
  waypoints: Waypoint[];
  assignedDriver?: AssignedDriver;
  estimatedTime?: number;
  distance?: number;
  status: 'active' | 'inactive' | 'completed';
  routeType?: 'pickup-orders' | 'orders';
  createdAt?: string;
  updatedAt?: string;
}

export interface RouteResponse {
  success: boolean;
  data?: Route | Route[];
  message?: string;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class RouteService {
  private baseEndpoint = '/routes';

  /**
   * Get all routes with optional filters
   */
  async getAllRoutes(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<RouteResponse> {
    try {
      console.log('RouteService: Fetching all routes with params:', params);
      const response = await axiosInstance.get(this.baseEndpoint, { params });
      console.log('RouteService: Routes API response:', response.data);
      
      if (response.data?.success) {
        return response.data;
      }
      
      // Handle different response structures
      if (Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data,
        };
      }
      
      return {
        success: true,
        data: [],
      };
    } catch (error) {
      console.error('Error fetching routes:', error);
      throw error;
    }
  }

  /**
   * Get a single route by ID
   */
  async getRouteById(routeId: string): Promise<RouteResponse> {
    try {
      console.log('RouteService: Fetching route with ID:', routeId);
      // Add cache-busting parameter to force fresh data
      const response = await axiosInstance.get(`${this.baseEndpoint}/${routeId}?t=${Date.now()}`);
      console.log('RouteService: Route API response:', response.data);
      
      if (response.data?.success) {
        return response.data;
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching route:', error);
      throw error;
    }
  }

  /**
   * Create a new route
   */
  async createRoute(routeData: Partial<Route>): Promise<RouteResponse> {
    try {
      console.log('RouteService: Creating new route:', routeData);
      const response = await axiosInstance.post(this.baseEndpoint, routeData);
      console.log('RouteService: Create route API response:', response.data);
      
      return {
        success: response.data?.success ?? true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Route created successfully',
      };
    } catch (error) {
      console.error('Error creating route:', error);
      throw error;
    }
  }

  /**
   * Update an existing route
   */
  async updateRoute(routeId: string, routeData: Partial<Route>): Promise<RouteResponse> {
    try {
      console.log('RouteService: Updating route:', routeId, routeData);
      const response = await axiosInstance.put(`${this.baseEndpoint}/${routeId}`, routeData);
      console.log('RouteService: Update route API response:', response.data);
      
      return {
        success: response.data?.success ?? true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Route updated successfully',
      };
    } catch (error) {
      console.error('Error updating route:', error);
      throw error;
    }
  }

  /**
   * Delete a route
   */
  async deleteRoute(routeId: string): Promise<RouteResponse> {
    try {
      console.log('RouteService: Deleting route:', routeId);
      const response = await axiosInstance.delete(`${this.baseEndpoint}/${routeId}`);
      console.log('RouteService: Delete route API response:', response.data);
      
      return {
        success: response.data?.success ?? true,
        message: response.data?.message || 'Route deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting route:', error);
      throw error;
    }
  }

  /**
   * Assign a driver to a route
   */
  async assignDriver(routeId: string, driverId: string): Promise<RouteResponse> {
    try {
      console.log('RouteService: Assigning driver to route:', { routeId, driverId });
      const response = await axiosInstance.post(`${this.baseEndpoint}/${routeId}/assign`, {
        driverId,
      });
      console.log('RouteService: Assign driver API response:', response.data);
      
      return {
        success: response.data?.success ?? true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Driver assigned successfully',
      };
    } catch (error) {
      console.error('Error assigning driver to route:', error);
      throw error;
    }
  }

  /**
   * Get routes assigned to a specific driver
   */
  async getDriverRoutes(driverId: string): Promise<RouteResponse> {
    try {
      console.log('RouteService: Fetching routes for driver:', driverId);
      const response = await axiosInstance.get(`${this.baseEndpoint}/driver/${driverId}`);
      console.log('RouteService: Driver routes API response:', response.data);
      
      if (response.data?.success) {
        return response.data;
      }
      
      if (Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data,
        };
      }
      
      return {
        success: true,
        data: [],
      };
    } catch (error) {
      console.error('Error fetching driver routes:', error);
      throw error;
    }
  }

  /**
   * Update route status
   */
  async updateRouteStatus(
    routeId: string,
    status: 'active' | 'inactive' | 'completed'
  ): Promise<RouteResponse> {
    try {
      console.log('RouteService: Updating route status:', { routeId, status });
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${routeId}/status`, {
        status,
      });
      console.log('RouteService: Update status API response:', response.data);
      
      return {
        success: response.data?.success ?? true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Route status updated successfully',
      };
    } catch (error) {
      console.error('Error updating route status:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const routeService = new RouteService();

// Export individual methods for convenience
export const getAllRoutes = (params?: any) => routeService.getAllRoutes(params);
export const getRouteById = (routeId: string) => routeService.getRouteById(routeId);
export const createRoute = (routeData: Partial<Route>) => routeService.createRoute(routeData);
export const updateRoute = (routeId: string, routeData: Partial<Route>) =>
  routeService.updateRoute(routeId, routeData);
export const deleteRoute = (routeId: string) => routeService.deleteRoute(routeId);
export const assignDriver = (routeId: string, driverId: string) =>
  routeService.assignDriver(routeId, driverId);
export const getDriverRoutes = (driverId: string) => routeService.getDriverRoutes(driverId);
export const updateRouteStatus = (routeId: string, status: 'active' | 'inactive' | 'completed') =>
  routeService.updateRouteStatus(routeId, status);

export default routeService;

